import { ApprovedFieldProjection } from "@/api/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import {
  ApprovedFieldForm,
  BackButton,
  duplicateWarnings,
  FIELD_CATEGORIES,
  FIELD_TYPE_OPTIONS,
  IMPORTANCE_OPTIONS,
  PageHeader,
} from "./shared";

export function CreateFieldFlow({
  form,
  fields,
  saving,
  duplicateOverride,
  onBack,
  onChange,
  onOverrideChange,
  onSubmit,
}: {
  form: ApprovedFieldForm;
  fields: ApprovedFieldProjection[];
  saving: boolean;
  duplicateOverride: boolean;
  onBack: () => void;
  onChange: (form: ApprovedFieldForm) => void;
  onOverrideChange: (value: boolean) => void;
  onSubmit: () => void;
}) {
  const warnings = duplicateWarnings(form.label, fields);
  const isBlockedByDuplicate = warnings.length > 0 && !duplicateOverride;

  return (
    <>
      <PageHeader
        title="Create approved field"
        subtitle="Once created, program managers can use this field in new application forms."
        action={<BackButton onClick={onBack} />}
      />
      <div className="space-y-4 p-4">
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          Existing active forms are not changed. This only makes the field available for future forms.
        </div>

        <form
          className="space-y-4 rounded-lg border bg-white p-4"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit();
          }}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <label className="text-sm font-medium">
              Field name <span className="text-red-600">*</span>
              <Input
                className="mt-1"
                value={form.label}
                onChange={(event) => onChange({ ...form, label: event.target.value })}
                placeholder="Example: Disability certificate number"
                required
              />
            </label>
            <label className="text-sm font-medium">
              Category <span className="text-red-600">*</span>
              <select
                className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                value={form.category}
                onChange={(event) => onChange({ ...form, category: event.target.value })}
              >
                {FIELD_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm font-medium">
              Field type <span className="text-red-600">*</span>
              <select
                className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                value={form.field_type}
                onChange={(event) =>
                  onChange({
                    ...form,
                    field_type: event.target.value as ApprovedFieldForm["field_type"],
                  })
                }
              >
                {FIELD_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm font-medium">
              Importance <span className="text-red-600">*</span>
              <select
                className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
                value={form.importance}
                onChange={(event) =>
                  onChange({
                    ...form,
                    importance: event.target.value as ApprovedFieldForm["importance"],
                  })
                }
              >
                {IMPORTANCE_OPTIONS.map((importance) => (
                  <option key={importance} value={importance}>
                    {importance}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="block text-sm font-medium">
            Review impact <span className="text-red-600">*</span>
            <Input
              className="mt-1"
              value={form.review_impact}
              onChange={(event) => onChange({ ...form, review_impact: event.target.value })}
              placeholder="Example: Triggers management re-review"
              required
            />
          </label>

          <label className="block text-sm font-medium">
            Business description <span className="text-red-600">*</span>
            <Textarea
              className="mt-1"
              value={form.description}
              onChange={(event) => onChange({ ...form, description: event.target.value })}
              placeholder="Shown to program managers."
              required
            />
          </label>

          <label className="block text-sm font-medium">
            Why is this field needed? <span className="text-red-600">*</span>
            <div className="mt-1 text-xs font-normal text-gray-500">
              Mandatory. This prevents field sprawl and creates an audit trail.
            </div>
            <Textarea
              className="mt-2"
              value={form.why_needed}
              onChange={(event) => onChange({ ...form, why_needed: event.target.value })}
              required
            />
          </label>

          {warnings.length > 0 ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <div className="font-medium">Possible duplicate field</div>
              <ul className="mt-1 list-disc pl-5">
                {warnings.map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
              <label className="mt-3 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={duplicateOverride}
                  onChange={(event) => onOverrideChange(event.target.checked)}
                />
                I checked this and still need a separate approved field.
              </label>
            </div>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onBack}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || isBlockedByDuplicate}>
              {saving ? "Creating..." : "Create field"}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}
